import React, { useEffect, useState, useCallback } from "react";
import { GET_BILL_RATE_CONFIGURATIONV1 } from "../constant";
import axios from "axios";
import BillRateTable from "../components/tables/BillRateTable";
import { currentDateFormat } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../redux/loaderAction";
import Loader from "../components/loader/Loader";

const PAGE_LIMIT = 10;
const PRIMARY_COLOR = "#F4A442";
const SECONDARY_COLOR = "#2D5A50";

/** Normalize v1 API rows for table display (nested objects + null safety) */
function mapBillRateListV1(items) {
  if (!Array.isArray(items)) return [];

  return items.map((item) => ({
    ...item,
    id: item?.id,
    base_bill_rate: item?.base_bill_rate,
    mileage_bill_rate: item?.mileage_bill_rate,
    start_date: item?.start_date,
    end_date: item?.end_date,
    serviceDetails: {
      service_name: item?.serviceDetails?.service_name ?? null,
      base_code: item?.serviceDetails?.base_code ?? null,
      milage_code: item?.serviceDetails?.milage_code ?? null,
      modifier: item?.serviceDetails?.modifier ?? null,
      milage_modifier: item?.serviceDetails?.milage_modifier ?? null,
    },
    payerDetails: {
      payer_name: item?.payerDetails?.payer_name ?? null,
    },
    stateDetails: {
      state_name: item?.stateDetails?.state_name ?? null,
    },
  }));
}

function parseBillRateV1Response(response) {
  const root = response?.data ?? {};
  const isOk =
    root?.success === true || root?.status === 200 || root?.status === "200";

  if (!isOk) {
    return { rows: [], pagination: null };
  }

  const envelope = root?.data;
  if (envelope != null && Array.isArray(envelope?.data)) {
    return {
      rows: mapBillRateListV1(envelope.data),
      pagination: envelope?.pagination ?? null,
    };
  }

  if (Array.isArray(envelope)) {
    return { rows: mapBillRateListV1(envelope), pagination: null };
  }

  if (Array.isArray(root?.data)) {
    return { rows: mapBillRateListV1(root.data), pagination: null };
  }

  return { rows: [], pagination: null };
}

function BillRateList() {
  const [billRateDetails, setBillRateDetails] = useState([]);
  const [searchPayor, setSearchPayor] = useState("");
  const [searchState, setSearchState] = useState("");
  const [searchBaseCode, setSearchBaseCode] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: PAGE_LIMIT,
  });
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loader.loading);
  const [currentDate] = useState(new Date());

  const fetchBillRateList = useCallback(
    async (page = 1, payer = "", state = "", baseCode = "") => {
      dispatch(setLoading(true));
      try {
        const payerParam = payer != null && String(payer).trim() !== "" ? String(payer).trim() : "";
        const stateParam = state != null && String(state).trim() !== "" ? String(state).trim() : "";
        const baseCodeParam =
          baseCode != null && String(baseCode).trim() !== "" ? String(baseCode).trim() : "";

        const params = {
          page,
          limit: PAGE_LIMIT,
          payer: payerParam,
          state: stateParam,
        };
        if (baseCodeParam) {
          params.base_code = baseCodeParam;
        }

        const response = await axios.get(GET_BILL_RATE_CONFIGURATIONV1, {
          params,
        });

        const { rows, pagination: paginationMeta } = parseBillRateV1Response(response);
        setBillRateDetails(rows);

        if (paginationMeta) {
          setPagination({
            currentPage: paginationMeta.currentPage ?? page,
            totalPages: Math.max(1, paginationMeta.totalPages ?? 1),
            totalRecords: paginationMeta.totalRecords ?? rows.length,
            recordsPerPage: paginationMeta.recordsPerPage ?? PAGE_LIMIT,
          });
        } else {
          setPagination({
            currentPage: page,
            totalPages: 1,
            totalRecords: rows.length,
            recordsPerPage: PAGE_LIMIT,
          });
        }
      } catch (error) {
        console.error("Error fetching bill rate list:", error);
        setBillRateDetails([]);
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          totalPages: 1,
          totalRecords: 0,
        }));
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    fetchBillRateList(currentPage, searchPayor, searchState, searchBaseCode);
  }, [currentPage, fetchBillRateList]);

  const handleSearch = async () => {
    setIsSearchLoading(true);
    setCurrentPage(1);
    try {
      await fetchBillRateList(1, searchPayor, searchState, searchBaseCode);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  const { currentPage: pg, totalPages, totalRecords, recordsPerPage } = pagination;
  const startIndex = (pg - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + (billRateDetails?.length || 0), totalRecords);

  const getPageNumbers = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, "ellipsis", totalPages];
  };

  const pageNumbers = getPageNumbers();

  return (
    <>
      <section>
        <div className="d-flex justify-content-between form-container">
          <div>
            <h2 className="fs-5 font-weight-600">Bill Rate List</h2>
          </div>
          <div>
            <span>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>

      <section className="filter-section">
        <div className="d-flex justify-content-between filter-container">
          <div className="schedule-filter">
            <div className="me-2">
              <label style={{ display: "block" }}>Payor</label>
              <input
                type="text"
                className="form-control"
                placeholder="Payor..."
                value={searchPayor}
                onChange={(e) => setSearchPayor(e.target.value)}
                style={{ maxWidth: "280px" }}
              />
            </div>
            <div className="me-2">
              <label style={{ display: "block" }}>State</label>
              <input
                type="text"
                className="form-control"
                placeholder="State..."
                value={searchState}
                onChange={(e) => setSearchState(e.target.value)}
                style={{ maxWidth: "280px" }}
              />
            </div>
            <div className="me-2">
              <label style={{ display: "block" }}>Service Code</label>
              <input
                type="text"
                className="form-control"
                placeholder="Base Code..."
                value={searchBaseCode}
                onChange={(e) => setSearchBaseCode(e.target.value)}
                style={{ maxWidth: "280px" }}
              />
            </div>
            <div className="me-2">
              <label style={{ display: "block" }}>&nbsp;</label>
              <button
                className="btn btn-primary rounded-1"
                type="button"
                style={{
                  background: "#31876f",
                  border: "unset",
                  height: "38px",
                  color: "#fff",
                  padding: "7px 16px",
                }}
                onClick={handleSearch}
                disabled={isSearchLoading}
              >
                {isSearchLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Searching...
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div>
          <BillRateTable tableData={billRateDetails} isLoading={loading} />
        </div>
        {totalPages > 1 && (
          <div
            className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 px-4 py-3 mt-0"
            style={{
              backgroundColor: "rgba(248, 249, 250, 0.8)",
              border: "1px solid #e9ecef",
              borderTop: "none",
              borderRadius: "0 0 0.25rem 0.25rem",
            }}
          >
            <p className="text-sm text-muted mb-0">
              Showing{" "}
              <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
                {billRateDetails?.length ? startIndex + 1 : 0}-{endIndex}
              </span>{" "}
              of{" "}
              <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
                {totalRecords}
              </span>{" "}
              results
            </p>
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-link p-2 text-secondary border-0 shadow-none"
                style={{ opacity: pg === 1 ? 0.35 : 1 }}
                disabled={pg === 1}
                onClick={() => handlePageChange(1)}
                title="First page"
                aria-label="First page"
              >
                <i className="pi pi-angle-double-left" />
              </button>
              <button
                type="button"
                className="btn btn-link p-2 text-secondary border-0 shadow-none me-1"
                style={{ opacity: pg === 1 ? 0.35 : 1 }}
                disabled={pg === 1}
                onClick={() => handlePageChange(pg - 1)}
                title="Previous page"
                aria-label="Previous page"
              >
                <i className="pi pi-angle-left" />
              </button>
              <div className="d-flex align-items-center gap-1">
                {pageNumbers.map((p, i) =>
                  p === "ellipsis" ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-muted">
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className="btn btn-sm fw-semibold rounded"
                      style={{
                        minWidth: 32,
                        height: 32,
                        backgroundColor: p === pg ? PRIMARY_COLOR : "transparent",
                        color: p === pg ? "#fff" : "#495057",
                        border: "none",
                        boxShadow: p === pg ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                      }}
                      onClick={() => handlePageChange(p)}
                      aria-label={`Page ${p}`}
                      aria-current={p === pg ? "page" : undefined}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>
              <button
                type="button"
                className="btn btn-link p-2 text-secondary border-0 shadow-none ms-1"
                style={{ opacity: pg === totalPages ? 0.35 : 1 }}
                disabled={pg === totalPages}
                onClick={() => handlePageChange(pg + 1)}
                title="Next page"
                aria-label="Next page"
              >
                <i className="pi pi-angle-right" />
              </button>
              <button
                type="button"
                className="btn btn-link p-2 text-secondary border-0 shadow-none"
                style={{ opacity: pg === totalPages ? 0.35 : 1 }}
                disabled={pg === totalPages}
                onClick={() => handlePageChange(totalPages)}
                title="Last page"
                aria-label="Last page"
              >
                <i className="pi pi-angle-double-right" />
              </button>
            </div>
          </div>
        )}
      </section>

      {loading && <Loader />}
    </>
  );
}

export default BillRateList;
